/**
 * CSAT Settings — configure SMS survey frequency, messaging, and phone number.
 */
import React from 'react';
import { Save, Phone, MessageSquare, Clock, RefreshCw } from 'lucide-react';
import { useGet, apiPut } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';

interface CsatConfig {
  enabled: boolean;
  fromNumber: string | null;
  trigger: string;
  maxPerMonth: number;
  everyNthTicket: number;
  messageTemplate: string;
  reminderDays: number;
}

export function CsatSettingsPage() {
  const { data, loading, error, refetch } = useGet<CsatConfig>('/api/v1/calls/csat/config');
  const [form, setForm] = React.useState<CsatConfig | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (loading) return <CardSkeleton lines={4} />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (!form) return null;

  function update<K extends keyof CsatConfig>(key: K, value: CsatConfig[K]) {
    setForm((prev) => prev ? { ...prev, [key]: value } : prev);
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    try {
      await apiPut('/api/v1/calls/csat/config', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">CSAT Survey Settings</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))]">Configure when SMS surveys are sent, how often, and the message template.</p>
      </header>

      {/* Enable/disable */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.enabled} onChange={(e) => update('enabled', e.target.checked)}
            className="h-5 w-5 rounded border-[hsl(var(--border))] text-brand-600 focus:ring-brand-500" />
          <div>
            <p className="text-sm font-semibold text-[hsl(var(--content))]">Enable CSAT Surveys</p>
            <p className="text-xs text-[hsl(var(--content-subtle))]">When enabled, SMS surveys are sent automatically based on the trigger below.</p>
          </div>
        </label>
      </div>

      {/* Phone number */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-brand-500" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Designated Phone Number</h3>
        </div>
        <p className="text-xs text-[hsl(var(--content-subtle))]">The Telnyx number surveys are sent from (and replies are received on).</p>
        <input value={form.fromNumber ?? ''} onChange={(e) => update('fromNumber', e.target.value || null)}
          placeholder="+18323016055"
          className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>

      {/* Frequency */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-brand-500" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Survey Frequency</h3>
        </div>

        <div>
          <label className="text-xs text-[hsl(var(--content-subtle))]">Trigger</label>
          <select value={form.trigger} onChange={(e) => update('trigger', e.target.value)}
            className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
            <option value="ticket_closed">When a ticket is closed</option>
            <option value="ticket_completed">When a ticket status = Completed</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[hsl(var(--content-subtle))]">Max per Month (per contact)</label>
            <input type="number" min="0" max="100" value={form.maxPerMonth}
              onChange={(e) => update('maxPerMonth', Number(e.target.value))}
              className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">Only send 1 survey per person per month (changeable). Even if they have 5 or 10 tickets.</p>
          </div>
          <div>
            <label className="text-xs text-[hsl(var(--content-subtle))]">Every Nth Ticket (0 = use max per month)</label>
            <input type="number" min="0" max="100" value={form.everyNthTicket}
              onChange={(e) => update('everyNthTicket', Number(e.target.value))}
              className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">Alternative: send every 5th ticket (set max per month to 0 to use this).</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-[hsl(var(--content-subtle))]">Reminder Days (0 = no reminder)</label>
          <input type="number" min="0" max="30" value={form.reminderDays}
            onChange={(e) => update('reminderDays', Number(e.target.value))}
            className="w-32 mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">Send a reminder if no response after this many days.</p>
        </div>
      </div>

      {/* Message template */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-brand-500" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Message Template</h3>
        </div>
        <p className="text-xs text-[hsl(var(--content-subtle))]">Supports: {'{{contactName}}'}, {'{{ticketId}}'}, {'{{companyName}}'}</p>
        <textarea value={form.messageTemplate} onChange={(e) => update('messageTemplate', e.target.value)} rows={3}
          className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
        <div className="rounded-lg bg-[hsl(var(--surface-subtle))] p-3 text-sm text-[hsl(var(--content-subtle))]">
          <p className="text-xs font-semibold mb-1">Preview:</p>
          <p>{form.messageTemplate
            .replace('{{contactName}}', 'John Smith')
            .replace('{{ticketId}}', '583382')
            .replace('{{companyName}}', 'Acme Corp')}</p>
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition-colors disabled:opacity-50">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
        </button>
        {saved && <span className="text-sm text-green-600">✓ Saved</span>}
      </div>
    </div>
  );
}

